import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface AdminContextType {
  isAdmin: boolean;
  adminToken: string | null;
  loginAdmin: (username: string, password: string) => Promise<boolean>;
  logoutAdmin: () => void;
}

// Create context with undefined default
const AdminContext = createContext<AdminContextType | undefined>(undefined);

// Custom hook to use the admin context
export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

interface AdminProviderProps {
  children: ReactNode;
}

// Simple admin credentials for development
const ADMIN_USERNAME = 'goatadmin';
const ADMIN_PASSWORD = 'password';

export const AdminProvider: React.FC<AdminProviderProps> = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [adminToken, setAdminToken] = useState<string | null>(null);

  useEffect(() => {
    // Check for existing admin token on mount
    const storedToken = localStorage.getItem('goatcardsAdminToken');
    if (storedToken) {
      try {
        // Verify token - in production this would call an API endpoint
        const tokenData = JSON.parse(atob(storedToken));
        if (tokenData && tokenData.exp > Date.now()) {
          setIsAdmin(true);
          setAdminToken(storedToken);
        } else {
          // Token expired, remove it
          localStorage.removeItem('goatcardsAdminToken');
        }
      } catch (error) {
        // Invalid token, remove it
        localStorage.removeItem('goatcardsAdminToken');
      }
    }
  }, []);

  const loginAdmin = async (username: string, password: string): Promise<boolean> => {
    try {
      // Simple credential check for development
      if (username === ADMIN_USERNAME && password === ADMIN_PASSWORD) {
        // Create secure token with expiration
        const tokenData = {
          username,
          iat: Date.now(),
          exp: Date.now() + (8 * 60 * 60 * 1000), // 8 hours
        };
        
        const token = btoa(JSON.stringify(tokenData));
        localStorage.setItem('goatcardsAdminToken', token);
        setAdminToken(token);
        setIsAdmin(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Admin login error:', error);
      return false;
    }
  };

  const logoutAdmin = () => {
    localStorage.removeItem('goatcardsAdminToken');
    setAdminToken(null);
    setIsAdmin(false);
  };

  return (
    <AdminContext.Provider value={{ isAdmin, adminToken, loginAdmin, logoutAdmin }}>
      {children}
    </AdminContext.Provider>
  );
};

export default AdminProvider; 